import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  useNavigate, Filter, SelectInput, SearchInput,
  useDataProvider, useNotify,
} from 'react-admin';
import {
  Box, Typography, Paper, Chip, Button, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Divider,
  Grid, Card, CardContent, Alert,
} from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import {
  CheckCircle, HourglassEmpty, Cancel, Warning, ArrowBack,
  Person, Category, CalendarToday, Flag, Description, Check,
} from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WorkflowTaskItem } from './WorkflowTasksPanel';
import { useMyTasks } from './useMyTasks';
import type { Task } from './types';

const STATUS_COLORS: Record<string, string> = {
  Pending: 'warning',
  InProgress: 'info',
  Completed: 'success',
  Cancelled: 'default',
  Escalated: 'error',
};

const PRIORITY_COLORS: Record<string, string> = {
  Low: 'default',
  Normal: 'info',
  High: 'warning',
  Urgent: 'error',
};

const NAMESPACE_COLORS: Record<string, string> = {
  HR: 'primary',
  Compliance: 'secondary',
  OHS: 'success',
  Finance: 'warning',
  SkillsDevelopment: 'info',
  System: 'default',
};

const TaskStatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'Completed': return <CheckCircle color="success" fontSize="small" />;
    case 'InProgress': return <HourglassEmpty color="info" fontSize="small" />;
    case 'Cancelled': return <Cancel color="disabled" fontSize="small" />;
    case 'Escalated': return <Warning color="error" fontSize="small" />;
    default: return <HourglassEmpty color="action" fontSize="small" />;
  }
};

const TaskFilter = () => (
  <Filter>
    <SelectInput source="status" choices={[
      { id: 'Pending', name: 'Pending' },
      { id: 'InProgress', name: 'In Progress' },
      { id: 'Completed', name: 'Completed' },
      { id: 'Cancelled', name: 'Cancelled' },
      { id: 'Escalated', name: 'Escalated' },
    ]} />
    <SelectInput source="priority" choices={[
      { id: 'Low', name: 'Low' },
      { id: 'Normal', name: 'Normal' },
      { id: 'High', name: 'High' },
      { id: 'Urgent', name: 'Urgent' },
    ]} />
    <SelectInput source="namespace" choices={[
      { id: 'HR', name: 'HR' },
      { id: 'OHS', name: 'OHS' },
      { id: 'Compliance', name: 'Compliance' },
      { id: 'Finance', name: 'Finance' },
      { id: 'SkillsDevelopment', name: 'Skills Development' },
      { id: 'System', name: 'System' },
    ]} />
    <SearchInput source="q" alwaysOn />
  </Filter>
);

export const TaskList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [view, setView] = useState<'table' | 'kanban'>('table');

  const { tasks, total, isLoading, completing, completeTask, refetch } = useMyTasks({
    sort: { field: 'expectedCompletionDate', order: 'asc' },
    perPage: 100,
  });

  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);

  const handleComplete = async (task: Task) => {
    setCompletingTaskId(task.id);
    try {
      await completeTask(task.id, { notes: 'Completed via task list' });
      notify('Task marked as completed', { type: 'success' });
      refetch();
    } catch (err) {
      notify('Failed to complete task', { type: 'error' });
    } finally {
      setCompletingTaskId(null);
    }
  };

  const pendingTasks = tasks.filter(t => t.status === 'Pending' || t.status === 'InProgress');
  const completedTasks = tasks.filter(t => t.status === 'Completed');

  return (
    <Box>
      <Box sx={{ mt: 2, mb: 3, px: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'grey.900' }}>Tasks</Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          icon={<HourglassEmpty />}
          label={`${pendingTasks.length} Pending`}
          color="warning"
          variant="outlined"
        />
        <Chip
          icon={<CheckCircle />}
          label={`${completedTasks.length} Completed`}
          color="success"
          variant="outlined"
        />

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/tasks/new')}
          startIcon={<Flag />}
        >
          New Task
        </Button>
      </Box>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Task</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Workflow</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map(task => (
              <TableRow key={task.id} hover>
                <TableCell>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                      onClick={() => navigate(`/tasks/${task.id}`)}
                    >
                      {task.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.description}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    icon={<TaskStatusIcon status={task.status} />}
                    label={task.status}
                    color={STATUS_COLORS[task.status] as ChipColor}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={task.priority}
                    color={PRIORITY_COLORS[task.priority] as ChipColor}
                    variant="filled"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{task.assignedTo.userName}</Typography>
                  <Typography variant="caption" color="text.secondary">{task.assignedTo.userRole}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{task.expectedCompletionDate}</Typography>
                  {new Date(task.expectedCompletionDate) < new Date() && task.status !== 'Completed' && (
                    <Chip size="small" label="Overdue" color="error" variant="filled" sx={{ mt: 0.5 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={task.namespace}
                    color={NAMESPACE_COLORS[task.namespace] as ChipColor}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  {task.status !== 'Completed' && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      onClick={() => handleComplete(task)}
                      disabled={completingTaskId === task.id}
                      startIcon={completingTaskId === task.id ? undefined : <Check />}
                    >
                      Complete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export const TaskShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [completing, setCompleting] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);

  const { tasks, total, isLoading: tasksLoading, completeTask, refetch } = useMyTasks({ perPage: 100 });

  useEffect(() => {
    if (id === 'new') {
      setLoading(false);
      return;
    }
    if (id && tasks.length > 0) {
      const found = tasks.find(t => t.id === id);
      if (found) {
        setTask(found);
        setLoading(false);
      } else if (!tasksLoading) {
        setLoading(false);
      }
    } else if (!tasksLoading) {
      setLoading(false);
    }
  }, [id, tasks, tasksLoading]);

  const handleComplete = async () => {
    if (!task) return;
    setCompleting(true);
    try {
      await completeTask(task.id, { notes: 'Completed via task detail' });
      notify('Task marked as completed', { type: 'success' });
      refetch();
      navigate('/tasks');
    } catch (err) {
      notify('Failed to complete task', { type: 'error' });
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <Box p={3}><Typography>Loading...</Typography></Box>;
  }

  if (id === 'new') {
    return (
      <Box p={3}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/tasks')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">Create New Task</Typography>
        </Box>
        <Alert severity="info">Task creation form would appear here. This feature requires backend integration.</Alert>
      </Box>
    );
  }

  if (!task) {
    return (
      <Box p={3}>
        <Alert severity="error">Task not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/tasks')} sx={{ mt: 2 }}>
          Back to Tasks
        </Button>
      </Box>
    );
  }

  const isOverdue = new Date(task.expectedCompletionDate) < new Date() && task.status !== 'Completed';

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/tasks')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">{task.name}</Typography>
          <Chip
            size="small"
            icon={<TaskStatusIcon status={task.status} />}
            label={task.status}
            color={STATUS_COLORS[task.status] as ChipColor}
            sx={{ mt: 1 }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {task.status !== 'Completed' && (
            <Button
              variant="contained"
              color="success"
              onClick={handleComplete}
              disabled={completing}
              startIcon={completing ? undefined : <Check />}
            >
              {completing ? 'Completing...' : 'Mark Complete'}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Task Details</Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{task.description}</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Flag color="action" />
                    <Typography variant="subtitle2" color="text.secondary">Priority</Typography>
                  </Box>
                  <Chip
                    label={task.priority}
                    color={PRIORITY_COLORS[task.priority] as ChipColor}
                    size="small"
                  />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CalendarToday color="action" />
                    <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                  </Box>
                  <Typography variant="body1">{task.expectedCompletionDate}</Typography>
                  {isOverdue && <Chip size="small" label="OVERDUE" color="error" sx={{ mt: 0.5 }} />}
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Category color="action" />
                    <Typography variant="subtitle2" color="text.secondary">Workflow</Typography>
                  </Box>
                  <Typography variant="body1">{task.workflowType}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    State: {task.workflowState}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Description color="action" />
                    <Typography variant="subtitle2" color="text.secondary">Step</Typography>
                  </Box>
                  <Typography variant="body1">{task.stepReference}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {task.taskReference}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {task.status === 'Completed' && task.actualCompletionDate && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">Completion</Typography>
                <Typography variant="body1">
                  Completed on: {task.actualCompletionDate}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Assignment</Typography>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Person color="action" />
                  <Typography variant="subtitle2" color="text.secondary">Assigned To</Typography>
                </Box>
                <Typography variant="body1">{task.assignedTo.userName}</Typography>
                <Typography variant="caption" color="text.secondary">{task.assignedTo.userRole}</Typography>
                <Typography variant="caption" color="text.secondary">ID: {task.assignedTo.userId}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Initiated By</Typography>
                <Typography variant="body1">
                  {task.initiatedBy?.userName || 'System'}
                </Typography>
                {task.initiatedBy?.userRole && (
                  <Typography variant="caption" color="text.secondary">{task.initiatedBy?.userRole}</Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                <Typography variant="body1">{task.owner?.serviceProviderName}</Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Classification</Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Namespace</Typography>
                <Chip
                  label={task.namespace}
                  color={NAMESPACE_COLORS[task.namespace] as ChipColor}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Subsystem</Typography>
                <Typography variant="body1">{task.subsystem}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">ID</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{task.id}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};